import React, {
  useCallback,
  useEffect,
  useState,
} from 'react';
import { Box } from '@styleseat/ui';

import { useLinkTo } from '../../../../hooks';

import analytics from '../../../../modules/analytics';
import { EmptyableImage } from './types';

import { ProfileImageSwiperContainer } from '../ProfileImageSwiper';
import { ClientProviderProfile } from '../../../../api/ProfileClientView';
import { PortalOverlay, PortalOverlayModal } from '../../../ui';
import { PRO_GALLERY } from '../../../../route-names';
import { PublicProvider } from '../../../../api/Providers';
import { ProfileGalleryImage } from '../../../../api/Providers/GalleryImages';
import { ProfileMobileImageGrid } from './ProfileMobileImageGrid';
import { ProfileDesktopImageGrid } from './ProfileDesktopImageGrid';

export type ProfileImageGridContainerProps = {
  galleryImages: Array<ProfileGalleryImage>;
  profile: ClientProviderProfile | PublicProvider;
  clickParams?: Record<string, any>;
  isEditMode?: boolean;
  isOwnProfile?: boolean;
  onAddImageClick?: () => void;
  isMobile?: boolean;
  inNewDesktopBreakpoint: boolean;
  inNewMobileBreakpoint: boolean;
};

export const ProfileImageGridContainer: React.FCWithChildren<
ProfileImageGridContainerProps
> = ({
  galleryImages,
  isEditMode,
  isOwnProfile,
  clickParams,
  profile,
  inNewDesktopBreakpoint,
  inNewMobileBreakpoint,
}) => {
  const linkTo = useLinkTo();
  const [showGalleryModal, setShowGalleryModal] = useState<boolean>(false);
  const [initialGalleryIndex, setInitialGalleryIndex] = useState<number>(-1);
  const providerId = profile.id;
  // tracking:
  useEffect(() => {
    analytics.track('client_revamp_proprofile_gallery_view', {
      ...clickParams,
      profile_type: 'provider',
      cover_image_click: false,
      provider_id: providerId,
    });
  }, []);

  const handleImageClick = useCallback((image: EmptyableImage, index: number) => {
    if (!isOwnProfile) {
      analytics.track('client_revamp_proprofile_gallery_image_click', {
        ...clickParams,
        profile_type: 'provider',
        cover_image_click: false,
        gallery_image_index: index,
      });
      setShowGalleryModal(true);
      setInitialGalleryIndex(index);
    }
  }, [
    clickParams,
    isOwnProfile,
  ]);
  const handleDesktopGalleryImagesClick = imageIndex => {
    analytics.track('client_revamp_proprofile_desktop_image_click', {
      ...clickParams,
      provider_id: providerId,
      gallery_image_index: imageIndex,
    });
    linkTo(PRO_GALLERY, {
      providerId,
      gallery_image_index: imageIndex,
    });
  };

  const handleGalleryClose = () => {
    setShowGalleryModal(false);
    setInitialGalleryIndex(-1);
  };

  const onSeeAllPhotosClick = () => {
    analytics.track('client_revamp_proprofile_gallery_see_all_photo_click', {
      ...clickParams,
      provider_id: profile.id,
    });
    linkTo(
      PRO_GALLERY,
      { providerId },
    );
  };

  return (
    <React.Fragment>
      <Box testID="pro-image-revamp">
        {inNewMobileBreakpoint && (
          <ProfileMobileImageGrid
            providerId={providerId}
            onImageClick={handleImageClick}
            onSeeAllPhotosClick={onSeeAllPhotosClick}
            galleryImages={galleryImages}
            totalImages={galleryImages.length}
          />
        )}
        {!inNewMobileBreakpoint && (
          <ProfileDesktopImageGrid
            providerId={providerId}
            galleryImages={galleryImages}
            inNewDesktopBreakpoint={inNewDesktopBreakpoint}
            onSeeAllPhotosClick={onSeeAllPhotosClick}
            onImageClick={handleDesktopGalleryImagesClick}
          />
        )}
      </Box>
      {showGalleryModal && (
        <PortalOverlay
          onBackdropClick={handleGalleryClose}
          name="gallery-image-modal"
        >
          <PortalOverlayModal
            position="fullScreen"
            testID="ProfileImageSwiperContainer"
          >
            <ProfileImageSwiperContainer
              galleryImages={galleryImages}
              initialIndex={initialGalleryIndex}
              onAvatarPress={handleGalleryClose}
              onClose={handleGalleryClose}
              profile={profile}
              isEditMode={isEditMode}
            />
          </PortalOverlayModal>
        </PortalOverlay>
      )}
    </React.Fragment>
  );
};
